import { Controller } from "@hotwired/stimulus"
import { Calendar } from "@fullcalendar/core"
import dayGridPlugin from "@fullcalendar/daygrid"
import timeGridPlugin from "@fullcalendar/timegrid"
import listPlugin from "@fullcalendar/list"
import interactionPlugin from "@fullcalendar/interaction"
import ptBrLocale from "@fullcalendar/core/locales/pt-br"

export default class extends Controller {
  static values = {
    eventsUrl:      String,
    newEventUrl:    String,
    showEventUrl:   String,
  }

  connect() {
    this.mobile = this.#isMobile()
    this.#initCalendar()
  }

  disconnect() {
    this.calendar?.destroy()
    this.calendar = null
  }

  // -- private --

  #isMobile() {
    return window.matchMedia("(max-width: 575.98px)").matches
  }

  #initCalendar() {
    this.calendar = new Calendar(this.element, {
      plugins: [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
      locale: ptBrLocale,
      events:       this.eventsUrlValue,
      height:       "auto",
      nowIndicator: true,
      selectable:   true,
      selectMirror: true,
      eventContent: this.#renderEventContent.bind(this),
      select:       this.#handleSelect.bind(this),
      eventClick:   this.#handleEventClick.bind(this),
      windowResize: this.#handleWindowResize.bind(this),
      views: {
        dayGridMonth: { displayEventTime: false },
      },
      ...this.#layoutOptions(this.mobile),
    })

    this.calendar.render()
  }

  // Returns the view + toolbar config for the given form factor. On phones a
  // month grid is unreadable, so default to an agenda list and stack the view
  // switcher into a footer toolbar to keep the header uncluttered.
  #layoutOptions(mobile) {
    if (mobile) {
      return {
        initialView: "listMonth",
        headerToolbar: {
          left: "prev,next",
          center: "title",
          right: "today",
        },
        footerToolbar: {
          center: "dayGridMonth,listMonth",
        },
      }
    }

    return {
      initialView: "dayGridMonth",
      headerToolbar: {
        left: "prev,next today",
        center: "title",
        right: "dayGridMonth,timeGridWeek,listMonth",
      },
      footerToolbar: false,
    }
  }

  // Swap the toolbar/view layout only when the viewport crosses the mobile
  // breakpoint, so FullCalendar's own resize handling does the rest.
  #handleWindowResize() {
    const mobile = this.#isMobile()
    if (mobile === this.mobile) return

    this.mobile = mobile
    const opts = this.#layoutOptions(mobile)
    this.calendar.setOption("headerToolbar", opts.headerToolbar)
    this.calendar.setOption("footerToolbar", opts.footerToolbar)
    this.calendar.changeView(opts.initialView)
  }

  #renderEventContent(arg) {
    const { event, timeText } = arg
    const customerName = event.extendedProps.customerName

    const wrapper = document.createElement("div")
    wrapper.className = "fc-event-inner"

    const mainLine = document.createElement("div")
    mainLine.className = "fc-event-main-line"

    if (timeText) {
      const timeEl = document.createElement("span")
      timeEl.className = "fc-event-time"
      timeEl.textContent = timeText
      mainLine.appendChild(timeEl)
    }

    const titleEl = document.createElement("span")
    titleEl.className = "fc-event-title"
    titleEl.textContent = event.title
    mainLine.appendChild(titleEl)

    wrapper.appendChild(mainLine)

    if (customerName) {
      const customerEl = document.createElement("div")
      customerEl.className = "fc-event-customer-name"
      customerEl.textContent = customerName
      wrapper.appendChild(customerEl)
    }

    return { domNodes: [wrapper] }
  }

  // Fired when the user clicks an existing event — navigate to its edit page.
  #handleEventClick(info) {
    info.jsEvent.preventDefault()
    window.location.href = this.showEventUrlValue.replace("__id__", info.event.id)
  }

  // Fired when the user clicks or drags over an empty time slot.
  // Builds the new-event URL with start_at (and end_at / all_day) pre-filled.
  #handleSelect(info) {
    const url = new URL(this.newEventUrlValue, window.location.origin)

    url.searchParams.set("event[start_at]", this.#toLocalDatetimeString(info.start))
    url.searchParams.set("return_to", "calendar")

    if (info.allDay) {
      url.searchParams.set("event[all_day]", "true")
    } else {
      url.searchParams.set("event[end_at]", this.#toLocalDatetimeString(info.end))
    }

    window.location.href = url.toString()
  }

  // Converts a JS Date to "YYYY-MM-DDTHH:mm" in the browser's local timezone,
  // which is the format expected by <input type="datetime-local">.
  #toLocalDatetimeString(date) {
    const pad = (n) => String(n).padStart(2, "0")
    return (
      `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
      `T${pad(date.getHours())}:${pad(date.getMinutes())}`
    )
  }
}
